import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { Box, CircularProgress, Alert } from "@mui/material";
import { TokenService } from "../../services/tokenService";

interface ProtectedRouteProps {
  children: React.ReactNode;
  screenName?: string;
  action?: "read" | "create" | "update" | "delete";
  requirePermission?: boolean;
}

const ProtectedRoute = ({
  children,
  screenName,
  action = "read",
  requirePermission = false,
}: ProtectedRouteProps) => {
  const {
    isAuthenticated,
    isAuthResolving,
    isLoading,
    backendToken,
    userAccountError,
    authFlowCompleted,
  } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  // TEMP local-only design bypass — inert unless REACT_APP_AUTH_BYPASS=true. Never commit / never deploy.
  if (process.env.REACT_APP_AUTH_BYPASS === "true") {
    return <>{children}</>;
  }

  // MSAL reports accounts as empty for a brief moment on every mount/refresh while it
  // re-hydrates the cached session. Wait that out before trusting isAuthenticated,
  // otherwise an already-logged-in user gets bounced to /login on every refresh.
  // Show loading while authentication is being determined
  if (isAuthResolving || isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (userAccountError && location.pathname !== "/login") {
    return <Navigate to="/login" replace />;
  }

  // If not authenticated, redirect to login with return url
  if (!isAuthenticated) {
    const isLogoutInProgress =
      sessionStorage.getItem("logout-in-progress") === "true";

    // Clear logout flag if it's set
    if (isLogoutInProgress) {
      sessionStorage.removeItem("logout-in-progress");
    }

    // Simple redirect to login - no complex timing logic
    const returnUrl =
      location.pathname !== "/login" ? location.pathname : "/dashboard";

    return (
      <Navigate to="/login" state={{ from: { pathname: returnUrl } }} replace />
    );
  }

  if (isAuthenticated && !backendToken && !authFlowCompleted) {
    // Still waiting for authentication flow to complete
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress />
        <Box>{t('components.protectedRoute.completingAuth')}</Box>
      </Box>
    );
  }

  if (isAuthenticated && !backendToken && authFlowCompleted) {
    // Authentication flow completed but no backend token - show warning but allow access
    return (
      <>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('components.protectedRoute.authInProgress')}
        </Alert>
        {children}
      </>
    );
  }

  // Check permissions if required
  if (requirePermission && screenName && backendToken) {
    const hasPermission = TokenService.hasPermission(
      backendToken,
      screenName,
      action
    );

    if (!hasPermission) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          p={3}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('components.protectedRoute.accessDenied')}
          </Alert>
          <Box textAlign="center">
            <p>
              {t('components.protectedRoute.noPermissionMessage', { action, screenName })}
            </p>
            <p>
              {t('components.protectedRoute.contactAdmin')}
            </p>
          </Box>
        </Box>
      );
    }
  }

  // If authenticated and has required permissions, render children
  return <>{children}</>;
};

export default ProtectedRoute;
